import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import Select from "react-select";
import PasswordStrengthIndicator from "../PasswordStrengthIndicator";
import { toast } from "react-toastify";
import "../../styles/addtaskstyle.css";

const UserCreationForm = ({
  onSubmit,
  cleanFormData,
  initialData,
  isLoading = false,
  isEdit = false,
  userRolesOptions = [],
  departmentOptions = [],
  groupOptions = [],
  users = [],
}) => {
  const [formData, setFormData] = useState(initialData);
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const fileInputRef = useRef(null);
  const size = 1; // MB

  useEffect(() => {
    setFormData(initialData);
    fileInputRef.current.value = null;
  }, [initialData]);

  const isValidMobileNumber = (number) => /^[0-9]{10}$/.test(number);
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleInputChange = (field, value) => {
    if (field === "Username") {
      const lettersOnly = /^[a-zA-Z\s]*$/;
      if (!lettersOnly.test(value)) return;
    }

    if (value === " " && formData[field] === "") return;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = size * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`Image size should not exceed ${size}MB.`);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        UserImage: file,
      }));
    }
  };

  const handlePasswordFocus = () => {
    if (formData.Password) {
      setShowPasswordStrength(true);
    }
  };

  const handlePasswordBlur = () => {
    setShowPasswordStrength(false);
  };

  const handlePasswordChange = (e) => {
    handleInputChange(e.target.name, e.target.value);
    setShowPasswordStrength(!!e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (
      !formData.Username ||
      !formData.EmailID ||
      !formData.MobileNumber ||
      !formData.RoleID ||
      (!isEdit && !formData.Password)
    ) {
      toast.error("Please enter required fields.");
      return;
    }

    if (!isValidEmail(formData.EmailID)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!isEdit && formData.Password.length < 6) {
      toast.error("Please enter a password that is at least 6 characters long.");
      return;
    }

    if (!isValidMobileNumber(formData.MobileNumber)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }

    onSubmit(formData);
  };

  const userOptions = useMemo(() => {
    return users.map((user) => ({
      value: user.userID,
      label: `${user.username} (${user.roleName})`,
    }));
  }, [users]);

  const userGroupOptions = useMemo(() => {
    return groupOptions.map((group) => ({
      value: group.groupID,
      label: group.name,
    }));
  }, [groupOptions]);

  const userRoleOptions = useMemo(() => {
    return userRolesOptions.map((role) => ({
      value: role.roleID,
      label: role.roleName,
    }));
  }, [userRolesOptions]);

  const userDepartmentOptions = useMemo(() => {
    return departmentOptions.map((department) => ({
      value: department.departmentID,
      label: department.name,
    }));
  }, [departmentOptions]);

  return (
    <div className="">
      <div className="text-center customheader">
        <h5 className="mb-0">{isEdit ? "Edit User" : "Create User"}</h5>
      </div>

      <Form onSubmit={handleSubmit} className="row">
        <div className="col-md-4 mb-4">
          <Form.Group>
            <Form.Label>User Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              name="Username"
              placeholder="Enter username"
              value={formData.Username}
              onChange={(e) => handleInputChange(e.target.name, e.target.value)}
            />
          </Form.Group>
        </div>

        <div className="col-md-4 mb-4">
          <Form.Group>
            <Form.Label>Email <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="email"
              name="EmailID"
              placeholder="Enter user email"
              value={formData.EmailID}
              onChange={(e) => handleInputChange(e.target.name, e.target.value)}
            />
          </Form.Group>
        </div>

        <div className="col-md-4 mb-4">
          <Form.Group className="positionrelative">
            <Form.Label>Password <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="Password"
              placeholder="Enter password"
              value={formData.Password}
              onChange={handlePasswordChange}
              onFocus={handlePasswordFocus}
              onBlur={handlePasswordBlur}
            />
            {showPasswordStrength && formData.Password && (
              <div className="mt-2 custompositionabsolute">
                <PasswordStrengthIndicator password={formData.Password} />
                <small className="text-muted">
                  Example of strong password: <code>N3wP@ss2023</code>
                </small>
              </div>
            )}
          </Form.Group>
        </div>

        <div className="col-md-4 mb-4">
          <Form.Group>
            <Form.Label>Mobile Number <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              name="MobileNumber"
              className="custom-mobilenumber"
              placeholder="Enter mobile number"
              value={formData.MobileNumber}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  handleInputChange(e.target.name, value);
                }
              }}
              maxLength={10}
            />
          </Form.Group>
        </div>

        <div className="col-md-4 mb-4">
          <Form.Group>
            <Form.Label>User Group <span className="text-danger">*</span></Form.Label>
            <Select
              name="GroupID"
              options={userGroupOptions}
              value={formData.GroupID}
              onChange={(e) => handleInputChange("GroupID", e)}
              placeholder="Select User Group"
              isClearable
              isSearchable
            />
          </Form.Group>
        </div>

        <div className="col-md-4 mb-4">
          <Form.Group>
            <Form.Label>User Department <span className="text-danger">*</span></Form.Label>
            <Select
              value={formData.DepartmentID}
              name="DepartmentID"
              options={userDepartmentOptions}
              onChange={(e) => handleInputChange("DepartmentID", e)}
              placeholder="Select User Department"
              isClearable
              isSearchable
            />
          </Form.Group>
        </div>

        <div className="col-md-4 mb-4">
          <Form.Group>
            <Form.Label>User Role <span className="text-danger">*</span></Form.Label>
            <Select
              value={formData.RoleID}
              name="RoleID"
              options={userRoleOptions}
              onChange={(e) => handleInputChange("RoleID", e)}
              placeholder="Select User Role"
              isClearable
              isSearchable
            />
          </Form.Group>
        </div>

        <div className="col-md-4 mb-4">
          <Form.Group controlId="Following">
            <Form.Label>User Following</Form.Label>
            <Select
              options={userOptions}
              isMulti
              isLoading={isLoading}
              name="Following"
              onChange={(selected) => handleInputChange("Following", selected)}
              value={formData.Following}
              className="shadow-sm option-list"
              placeholder="Select Following(s)"
              isClearable
              isSearchable
            />
          </Form.Group>
        </div>

        <div className="col-md-4 mb-4">
          <Form.Group>
            <Form.Label>User Followers</Form.Label>
            <Select
              options={userOptions}
              isMulti
              isLoading={isLoading}
              name="Followers"
              onChange={(selected) => handleInputChange("Followers", selected)}
              value={formData.Followers}
              className="shadow-sm"
              placeholder="Select Followers(s)"
              isClearable
              isSearchable
            />
          </Form.Group>
        </div>

<div className="col-md-4 mb-4">
  <Form.Group className="mb-3">
    <Form.Label>Upload User Photo</Form.Label>
    <Form.Control
      type="file"
      name="UserImage"
      onChange={handleFileChange}
      ref={fileInputRef}
      className="shadow-sm mb-2"
    />


    {formData.UserImage && (
      <div className="mt-2">
        {typeof formData.UserImage === "string" ? (
          <img
            src={formData.UserImage}
            alt="User"
            className="img-thumbnail"
            style={{ width: "120px", height: "120px", objectFit: "cover" }}
          />
        ) : (
          <img
            src={URL.createObjectURL(formData.UserImage)}
            alt="User"
            className="img-thumbnail"
            style={{ width: "120px", height: "120px", objectFit: "cover" }}
          />
        )}
      </div>
    )}

    <small className="ms-2 text-muted mt-2">
      Size:{" "}
      {formData.UserImage && typeof formData.UserImage !== "string"
        ? (formData.UserImage.size / (1024 * 1024)).toFixed(2)
        : 0}{" "}
      MB | Max size: {size}MB
    </small>
  </Form.Group>
</div>



        <div className="text-center mt-4">
          <Button
            variant="btn-primary"
            type="submit"
            className="px-4 me-3 btn-fontweight btn-btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner size="sm" />
            ) : isEdit ? (
              "Update User"
            ) : (
              "Add User"
            )}
          </Button>
          <Button
            className="px-4 btn-fontweight"
            onClick={cleanFormData}
            variant="secondary"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UserCreationForm;
